import MESSAGES from 'core/data-model/constants/bi-messages'

import {
  getCIESExceptionNotes,
  getCIESExplanationNotes,
  getCIESDiseaseNotes,
  getCIESDiseaseAdditionalNotes,
} from 'core/service/rider/benefits/exception-notes'
import { formatNumber } from 'core/service/lib/number-format'

const headers = [
  MESSAGES.BENEFIT_TITLE,
  MESSAGES.BENEFIT_BAHT,
  MESSAGES.RIDER_BENEFIT_DAY_OR_TIME,
  MESSAGES.RIDER_TOTAL_BENEFIT_BAHT_HAVE_STAR,
]

export const CIESBenefitSection = ({ rider, riderBenefitData, showDisclaimer, insured }) => {
  const { headTitle } = riderBenefitData
  return [
    { text: `${headTitle}`, style: 'sub-title' },
    { ...buildCIESExplanationNotes(rider) },
    ...buildDisclaimer(showDisclaimer),
    { ...buildExceptionNotes() },
    { ...buildCIESDiseaseNotes(rider) },
    { ...buildCIESDiseaseAdditionalNotes() },
  ]
}

const buildDisclaimer = (showDisclaimer) =>
  showDisclaimer ? `*${MESSAGES.DISCLAIMER_OVER_56_MUST_HAVE_MEDICAL_EXAM}` : ''

const buildExceptionNotes = () => {
  const exceptionNotes = getCIESExceptionNotes()
  const exceptionTitle = exceptionNotes.title
  const [
    normalExceptionTitle,
    extendExceptionDesc,
    warningTitle,
    warningDesc,
    remark,
    conditionTitle,
    conditionDesc,
  ] = exceptionNotes.exceptions

  return {
    pageBreak: 'before',
    stack: [
      {
        text: `${exceptionTitle}`,
        style: 'sub-title-2',
        margin: [0, 0, 0, 5],
      },
      {
        text: `${normalExceptionTitle}`,
        bold: true,
        margin: [0, 0, 0, 5],
      },
      {
        text: `${extendExceptionDesc[0]}`,
        decoration: 'underline',
      },
      {
        text: `${extendExceptionDesc[1]}`,
      },
      {
        ol: [
          {
            stack: [
              extendExceptionDesc[2][0],
              {
                table: {
                  body: [
                    ['1)', { text: `${extendExceptionDesc[2][1][0]}`, margin: [-5, 0, 0, 0] }],
                    ['2)', { text: `${extendExceptionDesc[2][1][1]}`, margin: [-5, 0, 0, 0] }],
                    ['3)', { text: `${extendExceptionDesc[2][1][2]}`, margin: [-5, 0, 0, 0] }],
                    ['4)', { text: `${extendExceptionDesc[2][1][3]}`, margin: [-5, 0, 0, 0] }],
                  ],
                },
                layout: 'noBorders',
                margin: [0, 0, 0, 0],
              },
            ],
          },
          {
            stack: [
              extendExceptionDesc[2][2],
              {
                table: {
                  body: [
                    ['1)', { text: `${extendExceptionDesc[2][3][0]}`, margin: [-5, 0, 0, 0] }],
                    ['2)', { text: `${extendExceptionDesc[2][3][1]}`, margin: [-5, 0, 0, 0] }],
                  ],
                },
                layout: 'noBorders',
                margin: [0, 0, 0, 0],
              },
            ],
          },
          {
            stack: [
              extendExceptionDesc[2][4],
              {
                table: {
                  body: [
                    ['1)', { text: `${extendExceptionDesc[2][5][0]}`, margin: [-5, 0, 0, 0] }],
                    ['2)', { text: `${extendExceptionDesc[2][5][1]}`, margin: [-5, 0, 0, 0] }],
                  ],
                },
                layout: 'noBorders',
                margin: [0, 0, 0, 0],
              },
            ],
          },
        ],
        margin: [0, 0, 0, 5],
      },
      {
        text: `${warningTitle}`,
        bold: true,
        margin: [0, 0, 0, 5],
      },
      {
        text: `${warningDesc[0]}`,
      },
      {
        ol: [...warningDesc[1]],
        margin: [0, 0, 0, 5],
      },
      {
        text: remark,
        margin: [0, 5],
      },
      {
        text: `${conditionTitle}`,
        bold: true,
        margin: [0, 5],
      },
      {
        text: `${conditionDesc[0]}`,
      },
      {
        text: `${conditionDesc[1]}`,
      },
    ],
  }
}

const buildCIESExplanationNotes = (rider) => {
  const selectedPlanGroup = rider?.selectedPlanGroup ?? {}
  const {
    CIES3 = false,
    CIES4 = false,
    CIESCB = false,
    CIESJN = false,
    CIESEI = false,
    CIESPE = false,
    CIESSC = false,
  } = selectedPlanGroup

  const [
    CIES3SumAssured,
    CIES4SumAssured,
    CIESCBSumAssured,
    CIESJNSumAssured,
    CIESPESumAssured,
    CIESEISumAssured,
    CIESSCSumAssured,
  ] = [CIES3, CIES4, CIESCB, CIESJN, CIESPE, CIESEI, CIESSC].map((p) => p?.riderSumAssured ?? 0)

  const explanationNotes = getCIESExplanationNotes()
  const [
    note_1,
    [
      note_1_1,
      [note_1_1_1],
      note_1_2,
      [note_1_2_1, note_1_2_2, note_1_2_3, note_1_2_4],
      note_1_3,
      [note_1_3_1, note_1_3_2],
    ],
    note_2,
    [note_2_1, [note_2_1_1], note_2_2, [note_2_2_1], note_2_3, [note_2_3_1]],
    remark_1,
  ] = explanationNotes.explanations

  return {
    stack: [
      {
        ol: [
          [
            { text: note_1, bold: true },
            {
              separator: ['1.'],
              ol: [
                [
                  {
                    table: {
                      widths: [350, '*'],
                      body: [
                        [
                          { text: note_1_1, bold: true },
                          {
                            text: `${
                              CIES3SumAssured ? formatNumber(CIES3SumAssured) : formatNumber(CIES4SumAssured)
                            } บาท`,
                            alignment: 'right',
                            bold: true,
                          },
                        ],
                      ],
                    },
                    layout: 'noBorders',
                  },
                  {
                    text: note_1_1_1,
                  },
                ],
                [
                  {
                    table: {
                      widths: [350, '*'],
                      body: [
                        [
                          { text: note_1_2, bold: true },
                          {
                            text: `${formatNumber(CIESCBSumAssured)} บาท`,
                            alignment: 'right',
                            bold: true,
                          },
                        ],
                      ],
                    },
                    layout: 'noBorders',
                  },
                  {
                    text: note_1_2_1,
                    bold: true,
                  },
                  {
                    text: note_1_2_2,
                  },
                  {
                    text: note_1_2_3,
                  },
                  {
                    text: note_1_2_4,
                  },
                ],
                [
                  {
                    table: {
                      widths: [350, '*'],
                      body: [
                        [
                          { text: note_1_3, bold: true },
                          {
                            text: CIESJNSumAssured ? `${formatNumber(CIESJNSumAssured)} บาท` : 'ไม่คุ้มครอง',
                            alignment: 'right',
                            bold: true,
                          },
                        ],
                      ],
                    },
                    layout: 'noBorders',
                  },
                  {
                    text: note_1_3_1,
                  },
                  {
                    text: note_1_3_2,
                  },
                ],
              ],
            },
          ],
          [
            { text: note_2, bold: true },
            {
              separator: ['2.'],
              ol: [
                [
                  {
                    table: {
                      widths: [350, '*'],
                      body: [
                        [
                          { text: note_2_1, bold: true },
                          {
                            text: `${formatNumber(CIESPESumAssured)} บาท`,
                            alignment: 'right',
                            bold: true,
                          },
                        ],
                      ],
                    },
                    layout: 'noBorders',
                  },
                  {
                    text: note_2_1_1,
                  },
                ],
                [
                  {
                    table: {
                      widths: [350, '*'],
                      body: [
                        [
                          { text: note_2_2, bold: true },
                          {
                            text: `${formatNumber(CIESEISumAssured)} บาท`,
                            alignment: 'right',
                            bold: true,
                          },
                        ],
                      ],
                    },
                    layout: 'noBorders',
                  },
                  {
                    text: note_2_2_1,
                  },
                ],
                [
                  {
                    table: {
                      widths: [350, '*'],
                      body: [
                        [
                          { text: note_2_3, bold: true },
                          {
                            text: `${formatNumber(CIESSCSumAssured)} บาท`,
                            alignment: 'right',
                            bold: true,
                          },
                        ],
                      ],
                    },
                    layout: 'noBorders',
                  },
                  {
                    text: note_2_3_1,
                  },
                ],
              ],
            },
          ],
        ],
        margin: [0, 0, 0, 5],
      },
      {
        text: [
          {
            text: remark_1.split(':')[0],
            bold: true,
            decoration: 'underline',
          },
          {
            text: ':',
            bold: true,
          },
          {
            text: remark_1.split(':')[1],
            bold: true,
          },
        ],
      },
    ],
  }
}

const buildCIESDiseaseNotes = (rider) => {
  const diseaseNotes = getCIESDiseaseNotes()
  const diseaseTitle = diseaseNotes.title
  const [
    majorStageTitle,
    majorStageDiseases,
    criticalCareTitle,
    criticalCareSubtitle,
    criticalCareDiseases,
    juvenileCriticalTitle,
    juvenileCriticalDiseases,
  ] = diseaseNotes.diseases
  const selectedPlanGroup = rider?.selectedPlanGroup ?? {}
  const { CIES3 = false } = selectedPlanGroup
  return {
    stack: [
      {
        text: `${diseaseTitle}`,
        style: 'sub-title-2',
        margin: [0, 5],
      },
      {
        text: `${majorStageTitle}`,
        decoration: 'underline',
        bold: true,
        margin: [0, 0, 0, 5],
      },
      {
        columns: [
          {
            ol: majorStageDiseases.slice(0, 27),
            width: 255,
          },
          {
            start: 28,
            ol: majorStageDiseases.slice(27, 53),
            width: '*',
          },
        ],
        columnGap: 15,
        margin: [5, 0, 0, 0],
      },
      {
        text: `${criticalCareTitle}`,
        decoration: 'underline',
        bold: true,
        margin: [0, 5],
      },
      {
        text: `${criticalCareSubtitle}`,
        margin: [0, 0, 0, 5],
      },
      {
        ol: criticalCareDiseases,
      },
      ...(CIES3
        ? [
            {
              text: `${juvenileCriticalTitle}`,
              decoration: 'underline',
              bold: true,
              margin: [0, 5],
            },
            {
              columns: [
                {
                  ol: juvenileCriticalDiseases.slice(0, 9),
                  width: 255,
                },
                {
                  start: 10,
                  ol: juvenileCriticalDiseases.slice(9, 17),
                  width: '*',
                },
              ],
              columnGap: 15,
              margin: [5, 0, 0, 0],
            },
          ]
        : []),
    ],
  }
}

const buildCIESDiseaseAdditionalNotes = () => {
  const diseaseNotes = getCIESDiseaseAdditionalNotes()
  const diseaseTitle = diseaseNotes.title
  const [
    earlyStageTitle,
    earlyStageDiseases,
    middleStageTitle,
    middleStageDiseases,
    specialStageTitle,
    specialStageDiseases,
    remark,
  ] = diseaseNotes.diseases
  return {
    stack: [
      {
        text: `${diseaseTitle}`,
        style: 'sub-title-2',
        margin: [0, 5],
      },
      {
        text: `${earlyStageTitle}`,
        decoration: 'underline',
        bold: true,
        margin: [0, 0, 0, 5],
      },
      {
        columns: [
          {
            ol: earlyStageDiseases.slice(0, 3),
            width: 255,
          },
          {
            start: 4,
            ol: earlyStageDiseases.slice(3, 6),
            width: '*',
          },
        ],
        columnGap: 15,
        margin: [5, 0, 0, 0],
      },
      {
        text: `${middleStageTitle}`,
        decoration: 'underline',
        bold: true,
        margin: [0, 5],
      },
      {
        columns: [
          {
            ol: middleStageDiseases.slice(0, 21),
            width: 255,
          },
          {
            start: 22,
            ol: middleStageDiseases.slice(21, 42),
            width: '*',
          },
        ],
        columnGap: 15,
        margin: [5, 0, 0, 0],
      },
      {
        text: `${specialStageTitle}`,
        decoration: 'underline',
        bold: true,
        margin: [0, 5],
      },
      {
        columns: [
          {
            ol: specialStageDiseases.slice(0, 2),
            width: 255,
          },
          {
            start: 3,
            ol: specialStageDiseases.slice(2, 4),
            width: '*',
          },
        ],
        columnGap: 15,
        margin: [5, 0, 0, 0],
      },
      {
        text: `${remark}`,
        margin: [0, 5],
      },
    ],
  }
}
